import { Amplify } from 'aws-amplify';
import { signUp, confirmSignUp, signIn, signOut, getCurrentUser, fetchAuthSession, resetPassword, confirmResetPassword, updatePassword, updateUserAttributes, signInWithRedirect } from 'aws-amplify/auth';
import cognitoConfig from '../config/cognito';

// Configure Amplify with Cognito settings - using the new v6 structure
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: cognitoConfig.userPoolId,
      userPoolClientId: cognitoConfig.userPoolWebClientId,
      region: cognitoConfig.region,
      // Include hosted UI configuration if domain is provided
      ...(cognitoConfig.hostedUI && {
        loginWith: {
          redirectSignIn: cognitoConfig.redirectSignIn,
          redirectSignOut: cognitoConfig.redirectSignOut
        }
      })
    }
  }
});

const cognitoService = {
  // Redirect to Cognito Hosted UI for sign in
  redirectToHostedUI: async () => {
    try {
      if (cognitoConfig.hostedUI) {
        window.location.href = cognitoConfig.hostedUI + '/login?client_id=' + 
          cognitoConfig.userPoolWebClientId + 
          '&response_type=code&scope=email+openid+profile&redirect_uri=' + 
          encodeURIComponent(cognitoConfig.redirectSignIn);
      } else {
        throw new Error('Cognito Hosted UI is not configured');
      }
    } catch (error) {
      console.error('Error redirecting to hosted UI:', error);
      throw error;
    }
  },

  // Sign up a new user
  signUp: async (email, password, attributes = {}) => {
    try {
      const { user } = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            ...attributes
          }
        }
      });
      return user;
    } catch (error) {
      console.error('Error signing up:', error);
      throw error;
    }
  },

  // Confirm sign up with verification code
  confirmSignUp: async (email, code) => {
    try {
      return await confirmSignUp({
        username: email,
        confirmationCode: code
      });
    } catch (error) {
      console.error('Error confirming sign up:', error);
      throw error;
    }
  },

  // Sign in user
  signIn: async (email, password) => {
    try {
      const { isSignedIn, nextStep } = await signIn({
        username: email,
        password
      });
      
      if (isSignedIn) {
        const user = await cognitoService.getCurrentUser();
        return user;
      } else {
        // Handle next steps like NEW_PASSWORD_REQUIRED or MFA
        return { nextStep };
      }
    } catch (error) {
      console.error('Error signing in:', error);
      throw error;
    }
  },

  // Sign out user
  signOut: async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  },

  // Get current authenticated user
  getCurrentUser: async () => {
    try {
      const user = await getCurrentUser();
      return user;
    } catch (error) {
      console.error('Error getting current user:', error);
      throw error;
    }
  },

  // Get current session (includes tokens)
  getCurrentSession: async () => {
    try {
      const session = await fetchAuthSession();
      return session;
    } catch (error) {
      console.error('Error getting current session:', error);
      throw error;
    }
  },

  // Get JWT token
  getJwtToken: async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString();
    } catch (error) {
      console.error('Error getting JWT token:', error);
      throw error;
    }
  },

  // Request password reset
  forgotPassword: async (email) => {
    try {
      return await resetPassword({
        username: email
      });
    } catch (error) {
      console.error('Error requesting password reset:', error);
      throw error;
    }
  },

  // Confirm password reset with code and new password
  forgotPasswordSubmit: async (email, code, newPassword) => {
    try {
      return await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword
      });
    } catch (error) {
      console.error('Error confirming password reset:', error);
      throw error;
    }
  },

  // Change password (when user is authenticated)
  changePassword: async (oldPassword, newPassword) => {
    try {
      return await updatePassword({
        oldPassword,
        newPassword
      });
    } catch (error) {
      console.error('Error changing password:', error);
      throw error;
    }
  },

  // Update user attributes
  updateUserAttributes: async (attributes) => {
    try {
      return await updateUserAttributes({
        userAttributes: attributes
      });
    } catch (error) {
      console.error('Error updating user attributes:', error);
      throw error;
    }
  }
};

export default cognitoService;
